import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The timeout ID for cleaning up the exiting slide.
   * @type {number|undefined}
   */
  #cleanupTimeout = undefined;

  /**
   * Whether a slide transition is in progress.
   * @type {boolean}
   */
  #isAnimating = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.suspend();
    this.current += 1;
    this.play();
  }

  previous() {
    this.suspend();
    this.current -= 1;
    this.play();
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  get slideDuration() {
    const raw = getComputedStyle(this).getPropertyValue('--slide-duration');
    const ms = parseInt(raw, 10);
    return Number.isNaN(ms) ? 500 : ms;
  }

  /**
   * Immediately finishes any in-progress slide transition.
   */
  #finishCurrentAnimation() {
    if (!this.#isAnimating) return;

    clearTimeout(this.#cleanupTimeout);
    this.#cleanupTimeout = undefined;

    const slides = this.refs.slides ?? [];

    // Find which slide was transitioning to active so we can finalize aria-hidden
    const activeIndex = slides.findIndex((s) => s.getAttribute('data-state') === 'active');

    slides.forEach((slide, index) => {
      slide.style.transition = 'none';
      slide.removeAttribute('data-state');
      if (activeIndex >= 0) {
        slide.setAttribute('aria-hidden', `${index !== activeIndex}`);
      }
    });

    // Force a reflow so the browser applies the no-transition cleanup
    // eslint-disable-next-line no-unused-expressions
    this.offsetHeight;

    slides.forEach((slide) => {
      slide.style.transition = '';
    });

    this.#isAnimating = false;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const count = slides.length;
    if (count === 0) return;

    const isSlideMode = this.getAttribute('data-transition') === 'slide';

    let oldRelative = this.#current % count;
    if (oldRelative < 0) oldRelative += count;

    this.#current = current;

    let relativeIndex = current % count;
    if (relativeIndex < 0) relativeIndex += count;

    if (isSlideMode && oldRelative !== relativeIndex) {
      // Cancel any in-progress animation instantly before starting a new one
      this.#finishCurrentAnimation();

      const oldSlide = slides[oldRelative];
      const newSlide = slides[relativeIndex];
      const duration = this.slideDuration;

      // Reset non-involved slides
      slides.forEach((slide, index) => {
        if (index !== oldRelative && index !== relativeIndex) {
          slide.removeAttribute('data-state');
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      // Keep old slide visible, keep new slide hidden (off-screen at translateY(100%))
      oldSlide?.setAttribute('aria-hidden', 'false');
      oldSlide?.removeAttribute('data-state');
      newSlide?.setAttribute('aria-hidden', 'true');
      newSlide?.removeAttribute('data-state');

      // Force reflow so browser registers the starting positions
      // eslint-disable-next-line no-unused-expressions
      this.offsetHeight;

      // Animate old slide out and new slide in
      this.#isAnimating = true;
      oldSlide?.setAttribute('data-state', 'exiting');
      newSlide?.setAttribute('data-state', 'active');

      // Clean up after transition completes
      this.#cleanupTimeout = setTimeout(() => {
        oldSlide?.removeAttribute('data-state');
        oldSlide?.setAttribute('aria-hidden', 'true');
        newSlide?.removeAttribute('data-state');
        newSlide?.setAttribute('aria-hidden', 'false');
        this.#isAnimating = false;
      }, duration + 50);
    } else {
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      });
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}